'use client';
import { memo } from 'react';
import Image from 'next/image';
import { ProcessStepHeading } from '../components/process-step-heading';
import { imageForStage } from '../lib/process-progress';
import type { ProcessStep } from '../lib/process-steps';

/*
 * Presentational pieces of the homepage "How it works" sequence. Everything here is driven by props
 * that change with scroll position, so nothing may add a CSS transition to those values: a
 * transition would make the visual lag behind the scrollbar and drift out of sync with the data
 * (which face shows which image, which step is highlighted).
 */

export function NavLoaderTrack({
  leftPx,
  widthPx,
  fillPct,
  opacity,
}: {
  leftPx: number;
  widthPx: number;
  fillPct: number;
  opacity: number;
}) {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute bottom-0 h-[4px] overflow-hidden rounded-2 bg-prople-50"
      style={{ left: `${leftPx}px`, width: `${widthPx}px`, opacity, willChange: 'left, width, opacity' }}
    >
      <div className="h-full rounded-2 bg-prople-500" style={{ width: `${fillPct}%` }} />
    </div>
  );
}

function StepCardBase({ step }: { step: ProcessStep }) {
  return (
    // Design: 52px heading, 16px gap, description (text-base/normal).
    <div className="isolate flex w-full max-w-[420px] flex-col gap-[16px]">
      <ProcessStepHeading number={step.number} title={step.title} className="mb-0" />
      <p className="ds-text-base-normal text-blue-bell-700">{step.description}</p>
    </div>
  );
}

/** Mobile description only — the title lives in the nav crossfade above. */
function MobileStepBodyBase({ step }: { step: ProcessStep }) {
  return <p className="ds-text-base-normal text-blue-bell-700">{step.description}</p>;
}

/** Mobile scroll visuals: crossfade between step images (no 3D flip). */
function MobileProcessVisualBase({
  opacities,
  maxHeight,
}: {
  opacities: readonly number[];
  /** CSS length that caps the frame on short phones so the pinned stage always fits the viewport. */
  maxHeight?: number | string;
}) {
  return (
    <div
      className="relative mx-auto w-full shrink-0"
      style={{ aspectRatio: '428 / 619', maxHeight }}
    >
      {opacities.map((opacity, k) => (
        <div
          key={k}
          className="absolute inset-0"
          style={{ opacity, willChange: 'opacity' }}
        >
          <Image
            src={imageForStage(k)}
            alt={k === 0 ? 'Proply process illustration' : ''}
            fill
            sizes="(max-width: 767px) 390px, 428px"
            className="object-contain object-center"
          />
        </div>
      ))}
    </div>
  );
}

type FlipImageProps = {
  frontSrc: string;
  backSrc: string;
  flipDeg: number;
};

function FlipImageFaces({ frontSrc, backSrc, flipDeg }: FlipImageProps) {
  return (
    <div
      className="absolute inset-0"
      style={{
        transformStyle: 'preserve-3d',
        transform: `rotateY(${flipDeg}deg)`,
        willChange: 'transform',
      }}
    >
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
      >
        <Image
          src={frontSrc}
          alt="Proply process illustration"
          fill
          sizes="640px"
          className="object-contain"
        />
      </div>
      <div
        aria-hidden
        className="absolute inset-0 flex items-center justify-center"
        style={{
          transform: 'rotateY(180deg)',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
        }}
      >
        <Image src={backSrc} alt="" fill sizes="640px" className="object-contain" />
      </div>
    </div>
  );
}

/**
 * Desktop-only mockup — 428×688, the designer's size (the images are 428:688, so this shows them in
 * full instead of letterboxed). The surrounding frame handles scaling.
 */
function DesktopFlipImageBase({ frontSrc, backSrc, flipDeg }: FlipImageProps) {
  return (
    <div
      className="relative mx-auto w-[428px]"
      style={{ aspectRatio: '428 / 688', perspective: '2000px' }}
    >
      <FlipImageFaces frontSrc={frontSrc} backSrc={backSrc} flipDeg={flipDeg} />
    </div>
  );
}

/** Invisible box that gives the image row its height while the real image is positioned absolutely. */
export function ImagePlaceholder() {
  return <div aria-hidden className="w-[428px]" style={{ aspectRatio: '428 / 688' }} />;
}

// Memoized so they skip every frame where their props are unchanged — most of the scroll, since the
// opacities and the flip angle only move inside the transition windows.
export const StepCard = memo(StepCardBase);
export const MobileStepBody = memo(MobileStepBodyBase);
export const MobileProcessVisual = memo(MobileProcessVisualBase);
export const DesktopFlipImage = memo(DesktopFlipImageBase);
